import { useEffect, useState } from "react";
import api from "../api/axiosClient";
import { CartContext } from "../components/CartContext";
import { useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

export function CartProvider({ children }) {
  const [cart, setCart] = useState([]);

  const location = useLocation();

  const isAdminRoute = location.pathname.startsWith("/admin");

  const { accessToken } = useAuth();
  const token = accessToken;
  const isLoggedIn = Boolean(token);
  /* ---------------- INIT CART ---------------- */

  useEffect(() => {

    // Skip cart initialization for admin pages
    if (isAdminRoute) {
      return;
    }

    if (token) {
      mergeAndLoad();
    } else {
      loadLocalCart();
    }

  }, [token, isAdminRoute]);

  const mergeAndLoad = async () => {
    const local = JSON.parse(localStorage.getItem("guest_cart")) || [];
    console.log("Merging local cart on init:", local);
    try {
    if (local.length) {
      await api.post("/cart/merge", { items: local });
      localStorage.removeItem("guest_cart");
    }

    loadBackendCart();
    }
    catch (e) {
      console.error("Failed to merge and load cart", e);
    }
  };


  const loadLocalCart = () => {
    const local = JSON.parse(localStorage.getItem("guest_cart")) || [];
    setCart(local);
  };

  const loadBackendCart = async () => {
    try {
      const res = await api.get("/cart");
      setCart(res.data);
    } catch (e) {
      console.error("Failed to load backend cart", e);
    }
  };

  /* ---------------- ADD ITEM ---------------- */

  const addItem = async (item) => {
    if (!item.variantId) {
      console.error("Missing variantId", item);
      return;
    }
    if (!token) {
      addLocal(item);
      return;
    }
    try {
      // optimistic UI update
      setCart(prev => {
        const existing = prev.find(i => i.variantId === item.variantId);
        if (existing) {
          return prev.map(i =>
            i.variantId === item.variantId
              ? { ...i, quantity: i.quantity + (item.quantity || 1) }
              : i
          );
        }
        return [...prev, { ...item, quantity: item.quantity || 1 }];
      });

      // backend sync
      await api.post("/cart/add", {
        variantId: item.variantId,
        quantity: item.quantity || 1,
        sku: item.sku,
        color: item.color,
        size: item.size,
        price: item.price
      });
    } catch (e) {
      console.error("Add to cart failed", e);
      // rollback
      await loadBackendCart();
    }
  };


  const addLocal = (item) => {
    const local = JSON.parse(localStorage.getItem("guest_cart")) || [];

    const existing = local.find(
      i => i.variantId === item.variantId
    );

    if (existing) {
      existing.quantity += item.quantity || 1;
      existing.imageUrl = existing.imageUrl || item.imageUrl;
    } else {
      local.push({
        variantId: item.variantId,
        productId: item.productId,
        sku: item.sku,
        color: item.color,
        size: item.size,
        price: item.price,
        imageUrl: item.imageUrl,
        quantity: item.quantity || 1
      });
    }
    setCart(local);
    localStorage.setItem("guest_cart", JSON.stringify(local));
    return;
  };

  /* ---------------- UPDATE QTY ---------------- */

  const updateQty = async (variantId, qty) => {
    if (!variantId) return;
    if (qty <= 0) return removeItem(variantId);

    if (!token) {
      const local = JSON.parse(localStorage.getItem("guest_cart")) || [];
      const item = local.find(i => i.variantId === variantId);
      if (item) item.quantity = qty;
      localStorage.setItem("guest_cart", JSON.stringify(local));
      setCart(local);
    } else {
      await api.put("/cart/update", { variantId, quantity: qty });
      loadBackendCart();
    }
  };

  /* ---------------- REMOVE ---------------- */

  const removeItem = async (variantId) => {
    if (!token) {
      const local = JSON.parse(localStorage.getItem("guest_cart")) || [];
      const updated = local.filter(i => i.variantId !== variantId);
      localStorage.setItem("guest_cart", JSON.stringify(updated));
      setCart(updated);
    } else {
      await api.post("/cart/remove", {
        variantId
      });
      await loadBackendCart();
    }
  };

  /* ---------------- MERGE ON LOGIN ---------------- */

  const syncOnLogin = async () => {
    console.log("Syncing cart on login");
    if (!token) return;

    const guest = JSON.parse(localStorage.getItem("guest_cart") || "[]");
    console.log("Merging guest cart on login:", guest.content);
    if (guest.length > 0) {
      console.log("Merging guest cart on login:", guest);
      await api.post("/cart/merge", { local: guest });
      localStorage.removeItem("guest_cart");
      await loadBackendCart();
    }

    const res = await api.get("/cart");
    setCart(res.data);
  };


  /* ---------------- HELPERS ---------------- */

  const getQty = (variantId) => {
    const item = cart.find(i => i.variantId === variantId);
    return item ? item.quantity : 0;
  };

  const totalItems = cart.reduce((a, b) => a + b.quantity, 0);

  /* ---------------- TOTALS ---------------- */

  const subtotal = cart.reduce((s, i) => s + i.price * i.quantity, 0);
  const shipping = subtotal > 500 ? 0 : 50;
  const taxes = "Included";
  const total = subtotal;

  const clearCart = () => {
    setCart([]);
    localStorage.removeItem("guest_cart"); // if you're storing it
  };


  return (
    <CartContext.Provider value={{
      cart,
      addItem,
      updateQty,
      removeItem,
      getQty,
      subtotal,
      shipping,
      taxes,
      total,
      totalItems,
      isLoggedIn,
      syncOnLogin,
      clearCart
    }}>
      {children}
    </CartContext.Provider>
  );
}
export default CartProvider;
